// src/pages/LoginPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchBots } from "../api/bots";

declare global {
  interface Window {
    google?: any;
  }
}

type LoginStage = "credentials" | "mfa";

const LoginPage: React.FC = () => {
  const { login, loginWithGoogle, loginWithMfaTotp, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [stage, setStage] = useState<LoginStage>("credentials");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const from = location.state?.from || "/app/bots";


   const redirectAfterLogin = async () => {
    try {
      const bots = await fetchBots();

      if (!bots || bots.length === 0) {
        // No bots yet → go straight to onboarding
        navigate("/onboarding/bots/new", { replace: true });
      } else {
        // At least one bot → go to bots list
        navigate("/app/bots", { replace: true });
      }
    } catch (err) {
      console.error("Failed to fetch bots after login, falling back to /app/bots", err);
      // Safe fallback if something goes wrong
      navigate("/app/bots", { replace: true });
    }
  };

  const handleSubmitCredentials: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setMfaError(null);

    try {
      const result = await login(email, password);
      if (result.kind === "success") {
        await redirectAfterLogin();
      } else {
        setMfaToken(result.mfaToken);
        setStage("mfa");
        setMfaCode("");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || t("login.loginError"));
    }
  };

  const handleSubmitMfa: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!mfaToken) return;

    setMfaError(null);

    try {
      await loginWithMfaTotp(mfaToken, mfaCode.trim());
      await redirectAfterLogin();
    } catch (err: any) {
      console.error(err);
      setMfaError(err.message || t("login.mfaError"));
      setMfaCode("");
    }
  };

  const handleBackToCredentials = () => {
    setStage("credentials");
    setMfaToken(null);
    setMfaCode("");
    setMfaError(null);
  };

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn("VITE_GOOGLE_CLIENT_ID is not set; Google login disabled.");
      return;
    }
    if (!window.google || !googleButtonRef.current) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        const idToken = response.credential;
        if (!idToken) return;
        try {
          setError(null);
          setMfaError(null);
          const result = await loginWithGoogle(idToken);
          if (result.kind === "success") {
            await redirectAfterLogin();
          } else {
            setMfaToken(result.mfaToken);
            setStage("mfa");
            setMfaCode("");
          }
        } catch (err: any) {
          console.error(err);
          setError(err.message || t("login.googleError"));
        }
      }
    });

    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: "outline",
      size: "large"
    });
  }, [from, loginWithGoogle, navigate, t]);

  return (
    <section className="auth-landing">
      <div className="lp-container">
        <div className="auth-layout">
          {/* Copy a sinistra */}
          <div className="auth-copy">
            <h1 className="auth-title">{t("login.pageTitle")}</h1>
            <p className="auth-subtitle">{t("login.subtitle")}</p>
            <ul className="auth-bullets">
              <li>{t("login.bullets.b1")}</li>
              <li>{t("login.bullets.b2")}</li>
              <li>{t("login.bullets.b3")}</li>
            </ul>
          </div>

          {/* Card con il form */}
          <div className="auth-card">
            {stage === "credentials" ? (
              <>
                <h2 className="auth-card-title">{t("login.cardTitle")}</h2>
                <form
                  className="form auth-form"
                  onSubmit={handleSubmitCredentials}
                >
                  {error && (
                    <div className="form-error onboarding-form-error-txt auth-form-error">{error}</div>
                  )}

                  <label className="form-field">
                    <span>{t("login.email")}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </label>

                  <label className="form-field">
                    <span>{t("login.password")}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </label>

                  <div style={{ textAlign: "right", marginTop: "-0.25rem" }}>
                    <Link
                      to="/forgot-password"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {t("login.forgotPassword", "Forgot password?")}
                    </Link>
                  </div>

                  <button
                    className="lp-btn lp-btn-primary auth-submit"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? t("login.submitting") : t("login.submit")}
                  </button>
                </form>

                <div className="auth-divider">
                  <span>{t("login.divider")}</span>
                </div>

                <div className="auth-google">
                  <div ref={googleButtonRef} />
                </div>

                <p className="auth-legal" style={{color: "black"}}>
                  {t("auth.legalNotice.p1")}{" "}
                <Link to="/terms">{t("auth.legalNotice.terms")}</Link>{" "}
                  {t("auth.legalNotice.and")}{" "}
                <Link to="/policy">{t("auth.legalNotice.privacy")}</Link>.
                </p>

                <p className="auth-switch">
                  {t("login.noAccount")}{" "}
                  <Link to="/register">{t("login.register")}</Link>
                </p>
              </>
            ) : (
              <>
                <h2 className="auth-card-title">{t("login.mfaTitle")}</h2>
                <p className="auth-subtitle">{t("login.mfaSubtitle")}</p>

                <form className="form auth-form" onSubmit={handleSubmitMfa}>
                  {mfaError && (
                    <div className="form-error onboarding-form-error-txt auth-form-error">{mfaError}</div>
                  )}

                  <label className="form-field">
                    <span>{t("login.mfaCodeLabel")}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      required
                    />
                  </label>

                  <button
                    className="lp-btn lp-btn-primary auth-submit"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? t("login.mfaSubmitting") : t("login.mfaSubmit")}
                  </button>

                  <button
                    type="button"
                    className="lp-btn lp-btn-ghost"
                    style={{ marginTop: "0.75rem" }}
                    onClick={handleBackToCredentials}
                    disabled={isLoading}
                  >
                    {t("login.backToCredentials")}
                  </button>
                </form>

                <p className="auth-switch">{t("login.mfaHelp")}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
